// Правая колонка, пока кандидат не выбран.
//
// Обычно на этом месте стоит приглашение выбрать строку — и оно здесь тоже есть. Но у воронки
// есть работа, которая НЕ принадлежит ни одному кандидату и при этом ждёт человека: спорные
// совпадения (сценарий 88). Явное совпадение по телефону система склеивает сама, спорное по
// ФИО она обязана вынести на ручное подтверждение — и вынести его некуда, кроме как сюда.
//
// Почему не отдельное представление: строка «пара карточек» — это действительно другая
// грамматика (правило 2), но у неё нет ни своей карточки, ни своих отборов, а решение по ней
// принимается за два нажатия прямо в строке. Представление ради очереди из двух-трёх пар —
// это восьмая вкладка в разделе, где их и так семь. Пустая правая колонка, наоборот, стоит
// без дела ровно в тот момент, когда рекрутёр смотрит на список и решает, чем заняться.
//
// Очереди нет — колонка ведёт себя как везде: одно приглашение, и оно не повторяет левое.

import { RiUserSearchLine } from '@remixicon/react';
import { Link, useNavigate } from 'react-router';

import { DetailPane } from '@/layout/DetailPane';
import * as Badge from '@/shared/ui/badge';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as LinkButton from '@/shared/ui/link-button';
import { EmptyState } from '@/shared/ui/screen-state';

import type { DuplicateReviewOut } from '../api/candidates';
import { useCan } from '../components/gates';
import { CANDIDATE_STATUS_TITLES } from '../dictionaries';
import { useDecideDuplicateMutation, useDuplicateReviewsQuery } from '../queries';
import type { HiringRoute } from '../route';
import { BLANK, PANEL_NEW_CANDIDATE, hiringHref, openPanelHref } from '../route';

const EDIT_PERMISSION = 'hiring.candidate.edit';
const MANAGE_PERMISSION = 'hiring.candidate.manage';

export function EmptyCandidateDetail({ route }: { route: HiringRoute }) {
  const navigate = useNavigate();
  const canEdit = useCan(EDIT_PERMISSION);
  const очередь = useDuplicateReviewsQuery('pending');
  const пары = очередь.data?.items ?? [];

  return (
    <DetailPane>
      {пары.length === 0 ? (
        <div className='flex h-full items-center justify-center'>
          <EmptyState
            icon={RiUserSearchLine}
            title='Кандидат не выбран'
            description='Выберите человека в списке слева — здесь откроется его карточка: контакты и квалификация, отклики с площадки, история этапов и оформление.'
            action={
              canEdit ? (
                <LinkButton.Root variant='primary' size='medium' underline asChild>
                  <Link to={openPanelHref(route, PANEL_NEW_CANDIDATE, BLANK)}>
                    Завести кандидата вручную
                  </Link>
                </LinkButton.Root>
              ) : undefined
            }
          />
        </div>
      ) : (
        <div className='flex flex-col gap-3 p-5'>
          <div className='flex flex-col gap-1'>
            <h3 className='text-label-md text-text-strong-950'>Спорные совпадения ждут решения</h3>
            <p className='text-paragraph-sm text-text-sub-600'>
              Совпадение по телефону система склеивает сама. Эти пары похожи фамилией и именем —
              решить, один это человек или два, машина не вправе. Пока решения нет, обе карточки
              живут отдельно и в воронке считаются как двое.
            </p>
          </div>
          {очередь.isError && <ErrorAlert error={очередь.error} />}
          {пары.map((пара) => (
            <ПараКарточек key={пара.id} пара={пара} route={route} />
          ))}
          <div className='pt-1'>
            <Button.Root
              type='button'
              variant='neutral'
              mode='ghost'
              size='xsmall'
              onClick={() => navigate(hiringHref(route.view, route.search))}
            >
              Выбрать кандидата в списке слева
            </Button.Root>
          </div>
        </div>
      )}
    </DetailPane>
  );
}

function ПараКарточек({ пара, route }: { пара: DuplicateReviewOut; route: HiringRoute }) {
  const canManage = useCan(MANAGE_PERMISSION);
  const решение = useDecideDuplicateMutation();

  return (
    <div className='flex flex-col gap-2 rounded-xl bg-bg-white-0 p-4 ring-1 ring-inset ring-stroke-soft-200'>
      <div className='flex flex-col gap-1.5'>
        {[пара.left, пара.right].map((карточка) => (
          <div key={карточка.id} className='flex min-w-0 items-center justify-between gap-2'>
            <Link
              to={hiringHref(route.view, { filters: {}, panels: [] }, карточка.id)}
              className='min-w-0 truncate text-label-sm text-primary-base underline тач:inline-flex тач:min-h-11 тач:items-center'
            >
              {карточка.display_name}
            </Link>
            <Badge.Root variant='lighter' color='gray' size='medium'>
              {CANDIDATE_STATUS_TITLES[карточка.status]}
            </Badge.Root>
          </div>
        ))}
      </div>
      <p className='text-paragraph-xs text-text-sub-600'>
        {(пара.signals ?? []).length > 0
          ? `Совпало: ${(пара.signals ?? []).join(', ')}`
          : 'Признак совпадения сервер не назвал — сравните карточки сами.'}
      </p>

      {решение.isError && <ErrorAlert error={решение.error} />}

      {canManage ? (
        <div className='flex flex-wrap items-center gap-2'>
          <Button.Root
            type='button'
            variant='primary'
            mode='filled'
            size='xsmall'
            onClick={() =>
              решение.mutate({ reviewId: пара.id, decision: 'merge', keepId: пара.left.id })
            }
          >
            {решение.isPending ? 'Решаем…' : 'Это один человек'}
          </Button.Root>
          <Button.Root
            type='button'
            variant='neutral'
            mode='stroke'
            size='xsmall'
            onClick={() => решение.mutate({ reviewId: пара.id, decision: 'separate' })}
          >
            Это разные люди
          </Button.Root>
        </div>
      ) : (
        <p className='text-paragraph-xs text-text-sub-600'>
          Склейка карточек — право старшего рекрутёра: она переносит отклики, заметки и метки на
          оставленную карточку и отменяется только заведением человека заново.
        </p>
      )}
      {canManage && (
        <p className='text-paragraph-xs text-text-sub-600'>
          «Один человек» оставит верхнюю карточку: отклики, заметки, метки и квалификация переедут
          на неё, вторая станет ссылкой на первую.
        </p>
      )}
    </div>
  );
}
